import type { ForceGenerator } from "./ForceGenerator.ts";

export interface PhysicsBodyOptions {
  mass?: number;
  friction?: number;
  maxVelocity?: number;
  maxValue?: number;
  /** Velocity multiplier applied when a maxValue clamp triggers (default 1 = no change). */
  restitution?: number;
  sleepEpsilon?: number;
}

export interface PhysicsStepResult {
  value: number;
  velocity: number;
}

/**
 * Single-owner physics state: one value + one velocity, driven by any number
 * of additive forces plus an optional multiplicative friction/damping step.
 *
 * Not a StringModule, no DOM dependency — callers own the instance and drive
 * it from their own onFrame loop.
 */
export class PhysicsBody {
  value = 0;
  velocity = 0;

  mass: number;
  friction: number;
  maxVelocity?: number;
  maxValue?: number;
  restitution: number;
  sleepEpsilon: number;

  private forces: ForceGenerator[] = [];

  constructor(options: PhysicsBodyOptions = {}) {
    this.mass = options.mass ?? 1;
    this.friction = options.friction ?? 0;
    this.maxVelocity = options.maxVelocity;
    this.maxValue = options.maxValue;
    this.restitution = options.restitution ?? 1;
    this.sleepEpsilon = options.sleepEpsilon ?? 0.01;
  }

  addForce(force: ForceGenerator): this {
    this.forces.push(force);
    return this;
  }

  removeForce(force: ForceGenerator): this {
    const index = this.forces.indexOf(force);
    if (index !== -1) this.forces.splice(index, 1);
    return this;
  }

  applyImpulse(deltaVelocity: number): void {
    this.velocity += deltaVelocity;
  }

  step(dt = 1): PhysicsStepResult {
    let netForce = 0;
    for (let i = 0; i < this.forces.length; i++) {
      netForce += this.forces[i].compute(this);
    }

    this.velocity += (netForce / this.mass) * dt;
    this.velocity *= 1 - this.friction;

    if (this.maxVelocity !== undefined) {
      if (this.velocity > this.maxVelocity) this.velocity = this.maxVelocity;
      else if (this.velocity < -this.maxVelocity) this.velocity = -this.maxVelocity;
    }

    this.value += this.velocity * dt;

    if (this.maxValue !== undefined) {
      if (this.value > this.maxValue) {
        this.value = this.maxValue;
        this.velocity *= this.restitution;
      } else if (this.value < -this.maxValue) {
        this.value = -this.maxValue;
        this.velocity *= this.restitution;
      }
    }

    return { value: this.value, velocity: this.velocity };
  }

  isAtRest(epsilon = this.sleepEpsilon): boolean {
    return Math.abs(this.velocity) < epsilon && Math.abs(this.value) < epsilon;
  }

  reset(): void {
    this.value = 0;
    this.velocity = 0;
  }
}
